import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Circle, ChevronLeft, Target, RefreshCw, Clock, AlertTriangle, ArrowUp, ArrowDown, Minus, User } from 'lucide-react'
import { employeeApi } from '../../lib/employeeApi'
import MobileLayout from '../../components/MobileLayout'

interface Mission {
  id: string
  title: string
  description?: string | null
  assignedToIds: string[]
  assignedToNames: string[]
  periodStart?: string | null
  periodEnd?: string | null
  priority: string
  status: string
  completedAt?: string | null
  completedByName?: string | null
}

const PRIORITY_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  urgent: { label: 'Urgent',  color: 'text-red-600 bg-red-50 border-red-200',         icon: AlertTriangle },
  high:   { label: 'Haute',   color: 'text-orange-600 bg-orange-50 border-orange-200', icon: ArrowUp },
  normal: { label: 'Normale', color: 'text-blue-600 bg-blue-50 border-blue-200',       icon: Minus },
  low:    { label: 'Basse',   color: 'text-gray-500 bg-gray-50 border-gray-200',       icon: ArrowDown },
}

const MONTHS = ['jan','fév','mar','avr','mai','juin','juil','août','sep','oct','nov','déc']
function fmtDate(d: string) {
  const dt = new Date(d + 'T12:00:00')
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]}`
}
function formatPeriod(start?: string | null, end?: string | null) {
  if (start && end) return `${fmtDate(start)} → ${fmtDate(end)}`
  if (end)   return `jusqu'au ${fmtDate(end)}`
  if (start) return `à partir du ${fmtDate(start)}`
  return null
}

export default function MobileMissions() {
  const navigate = useNavigate()
  const [missions, setMissions] = useState<Mission[]>([])
  const [today, setToday] = useState('')
  const [loading, setLoading] = useState(false)
  const [completing, setCompleting] = useState<string | null>(null)
  const [tab, setTab] = useState<'todo' | 'done'>('todo')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await employeeApi.get('/employee/me/missions')
      setMissions(r.data.missions || [])
      setToday(r.data.today || '')
    } catch { } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    load()
    const interval = setInterval(load, 30000)
    const onVisible = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible) }
  }, [load])

  const complete = async (mission: Mission) => {
    setCompleting(mission.id)
    try {
      await employeeApi.post(`/employee/me/missions/${mission.id}/complete`)
      setMissions(prev => prev.map(m =>
        m.id === mission.id ? { ...m, status: 'done', completedAt: new Date().toISOString() } : m
      ))
    } catch { } finally { setCompleting(null) }
  }

  const pending = missions.filter(m => m.status === 'todo')
  const done    = missions.filter(m => m.status === 'done')

  const isOverdue = (m: Mission) => m.periodEnd && m.periodEnd < today && m.status === 'todo'
  const isToday   = (m: Mission) => m.periodEnd === today

  return (
    <MobileLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b px-4 flex items-center gap-3 sticky top-0 z-10" style={{ paddingTop: 'max(16px, env(safe-area-inset-top))', paddingBottom: '16px' }}>
          <button onClick={() => navigate('/app/home')} className="p-2 rounded-lg hover:bg-gray-100">
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="font-bold text-gray-900 flex items-center gap-2">
              <Target size={18} className="text-purple-600" /> Missions
            </h1>
            <p className="text-xs text-gray-500">{pending.length} à faire · {done.length} terminée{done.length > 1 ? 's' : ''}</p>
          </div>
          <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100">
            <RefreshCw size={16} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b bg-white sticky top-[69px] z-10">
          <button onClick={() => setTab('todo')}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${tab === 'todo' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'}`}>
            À faire {pending.length > 0 && <span className="ml-1 bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full">{pending.length}</span>}
          </button>
          <button onClick={() => setTab('done')}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${tab === 'done' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}>
            Terminées {done.length > 0 && <span className="ml-1 bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded-full">{done.length}</span>}
          </button>
        </div>

        <div className="p-4 space-y-3 pb-8">
          {loading ? (
            <div className="text-center py-16 text-gray-400">
              <RefreshCw size={32} className="mx-auto mb-2 animate-spin text-purple-500" />
              <p className="text-sm">Chargement...</p>
            </div>
          ) : tab === 'todo' ? (
            pending.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <CheckCircle2 size={56} className="mx-auto mb-3 text-gray-200" />
                <p className="font-medium">Aucune mission à faire</p>
                <p className="text-sm mt-1">Toutes tes missions sont terminées !</p>
              </div>
            ) : pending.map(m => {
              const pCfg = PRIORITY_CONFIG[m.priority] || PRIORITY_CONFIG.normal
              const PIcon = pCfg.icon
              const overdue = isOverdue(m)
              const todayDue = isToday(m)
              const period = formatPeriod(m.periodStart, m.periodEnd)
              return (
                <div key={m.id} className={`bg-white rounded-2xl border overflow-hidden shadow-sm ${overdue ? 'border-red-300' : todayDue ? 'border-purple-300' : 'border-gray-200'}`}>
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <button onClick={() => complete(m)} disabled={completing === m.id} className="mt-0.5 flex-shrink-0">
                        {completing === m.id
                          ? <RefreshCw size={24} className="text-purple-500 animate-spin" />
                          : <Circle size={24} className="text-gray-300" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{m.title}</p>
                        {m.description && <p className="text-sm text-gray-500 mt-0.5">{m.description}</p>}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${pCfg.color}`}>
                            <PIcon size={10} /> {pCfg.label}
                          </span>
                          {m.assignedToNames.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                              <User size={10} /> {m.assignedToNames.join(' & ')}
                            </span>
                          )}
                          {period && (
                            <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${overdue ? 'bg-red-100 text-red-700' : todayDue ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                              <Clock size={10} />
                              {overdue && 'En retard · '}{todayDue && "Fin auj. · "}{period}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => complete(m)} disabled={completing === m.id}
                      className="mt-3 w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50 text-sm">
                      {completing === m.id ? 'En cours...' : '✓ Marquer comme terminé'}
                    </button>
                  </div>
                </div>
              )
            })
          ) : (
            done.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Target size={56} className="mx-auto mb-3 text-gray-200" />
                <p className="font-medium">Aucune mission terminée</p>
              </div>
            ) : done.map(m => {
              const period = formatPeriod(m.periodStart, m.periodEnd)
              return (
                <div key={m.id} className="bg-green-50 rounded-2xl border border-green-100 p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={24} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-500 line-through text-sm">{m.title}</p>
                      {m.description && <p className="text-xs text-gray-400 mt-0.5 line-through">{m.description}</p>}
                      <div className="flex flex-wrap gap-2 mt-1">
                        {m.assignedToNames.length > 0 && <span className="text-xs text-gray-400">{m.assignedToNames.join(' & ')}</span>}
                        {period && <span className="text-xs text-gray-400"><Clock size={10} className="inline mr-1" />{period}</span>}
                      </div>
                      {m.completedAt && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ Terminée à {new Date(m.completedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          {m.completedByName && ` par ${m.completedByName}`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </MobileLayout>
  )
}
